import { useEffect, useMemo, useState } from "react";
import jsPDF from "jspdf";
import { supabase } from "../supabaseClient";
import YomaLogo from "../components/YomaLogo";
import { YOMA_LOGO_SVG } from "../utils/yomaLogoSvg";

const GOLD       = "#C9A96E";
const GOLD_PALE  = "#F0DEB4";
const GOLD_BG    = "#FEF9EE";
const INK        = "#1C1915";
const INK_SOFT   = "#4A4540";
const INK_MUTED  = "#9A9390";
const BORDER     = "#E7E1D8";
const SOFT_BG    = "#F7F5F2";
const WHITE      = "#FFFFFF";
const GREEN      = "#1A7A44";
const GREEN_BG   = "#E8F6EF";
const RED        = "#B91C1C";
const RED_BG     = "#FEF2F2";

function fmtMontant(n, deviseLabel) {
  if (n == null || n === "") return "—";
  return Number(n).toLocaleString("fr-FR") + " " + deviseLabel;
}

// ——— Moteur de devis (4 zones) ———
const DEVIS = {
  tarif_3d_par_piece: 2500,
  forfait_dossier_technique: 15000,
  forfait_dossier_administratif: 8000,
  suivi_chantier_pct: 10,
  suivi_renovation_pct: 9,
  provision_imprevus_pct_default: 5,
};

// ——— Marge intelligente FF&E (recommandée par critères) ———
const MARGE_FFE_BASE_PCT = 15;
const MARGE_FFE_MIN_PCT = 8;
const MARGE_FFE_MAX_PCT = 30;

const FFEE_TYPE_PRODUIT = [
  { id: "", label: "— Type —", ajust: 0 },
  { id: "mobilier", label: "Mobilier", ajust: 2 },
  { id: "luminaire", label: "Luminaire", ajust: 1 },
  { id: "textile", label: "Textile", ajust: 0 },
  { id: "art", label: "Art / objet", ajust: -1 },
  { id: "equipement", label: "Équipement", ajust: 1 },
  { id: "decoration", label: "Décoration", ajust: 0.5 },
];

const FFEE_PROVENANCE = [
  { id: "", label: "— Provenance —", ajust: 0 },
  { id: "local", label: "Locale", ajust: 0 },
  { id: "national", label: "Nationale", ajust: 0.5 },
  { id: "import", label: "Import", ajust: 2 },
  { id: "luxe", label: "Luxe / créateur", ajust: 2.5 },
];

const FFEE_QUALITE = [
  { id: "", label: "— Qualité —", ajust: 0 },
  { id: "standard", label: "Standard", ajust: 0 },
  { id: "premium", label: "Premium", ajust: 2 },
  { id: "luxe", label: "Luxe", ajust: 3 },
];

const FFEE_AUTHENTICITE = [
  { id: "", label: "— Authenticité —", ajust: 0 },
  { id: "standard", label: "Standard", ajust: 0 },
  { id: "piece_unique", label: "Pièce unique", ajust: 2 },
  { id: "vintage_art", label: "Vintage / art", ajust: 1.5 },
];

/** Ajustement selon tranche de prix (gros montant = marge légèrement réduite). */
function ajustementPrix(prixHT) {
  if (!prixHT || prixHT <= 0) return { ajust: 0, lib: "" };
  if (prixHT < 3000) return { ajust: 1, lib: "Petit montant" };
  if (prixHT < 15000) return { ajust: 0, lib: "Montant moyen" };
  if (prixHT < 50000) return { ajust: -0.5, lib: "Gros montant" };
  return { ajust: -1, lib: "Très gros montant" };
}

/**
 * Calcule la marge recommandée pour une ligne FF&E selon les 5 critères.
 * Retourne margeBasePct, ajustements, margeFinalePct (bornée 8–30 %), fourchette, justification.
 */
function calculMargeIntelligente(ligne) {
  const prixHT = parseFloat(ligne.prix_achat_ht) || 0;
  const margeBasePct = MARGE_FFE_BASE_PCT;
  const ajustements = [];
  let totalAjust = 0;

  const typeAjust = FFEE_TYPE_PRODUIT.find((t) => t.id === (ligne.type_produit || ""))?.ajust ?? 0;
  if (typeAjust !== 0) {
    ajustements.push({ libelle: "Type produit", valeur: typeAjust });
    totalAjust += typeAjust;
  }

  const prixA = ajustementPrix(prixHT);
  if (prixA.ajust !== 0) {
    ajustements.push({ libelle: prixA.lib, valeur: prixA.ajust });
    totalAjust += prixA.ajust;
  }

  const provAjust = FFEE_PROVENANCE.find((p) => p.id === (ligne.provenance_produit || ""))?.ajust ?? 0;
  if (provAjust !== 0) {
    ajustements.push({ libelle: "Provenance", valeur: provAjust });
    totalAjust += provAjust;
  }

  const qualAjust = FFEE_QUALITE.find((q) => q.id === (ligne.qualite_produit || ""))?.ajust ?? 0;
  if (qualAjust !== 0) {
    ajustements.push({ libelle: "Qualité", valeur: qualAjust });
    totalAjust += qualAjust;
  }

  const authAjust = FFEE_AUTHENTICITE.find((a) => a.id === (ligne.authenticite_produit || ""))?.ajust ?? 0;
  if (authAjust !== 0) {
    ajustements.push({ libelle: "Authenticité", valeur: authAjust });
    totalAjust += authAjust;
  }

  let margeFinalePct = margeBasePct + totalAjust;
  margeFinalePct = Math.max(MARGE_FFE_MIN_PCT, Math.min(MARGE_FFE_MAX_PCT, Math.round(margeFinalePct * 10) / 10));

  const fourchette = { min: MARGE_FFE_MIN_PCT, max: MARGE_FFE_MAX_PCT };
  const justification =
    ajustements.length === 0
      ? `Base ${margeBasePct} % (fourchette ${fourchette.min}–${fourchette.max} %)`
      : `Base ${margeBasePct} % + ${ajustements.map((a) => (a.valeur >= 0 ? "+" : "") + a.valeur + " % " + a.libelle).join(", ")} → ${margeFinalePct} % (fourchette ${fourchette.min}–${fourchette.max} %)`;

  return { margeBasePct, ajustements, margeFinalePct, fourchette, justification };
}

/** Pour une ligne FF&E, retourne marge utilisée (recommandée ou override), total marge DH, total client DH, justification. */
function computeLigneFFE(ligne) {
  const prixHT = parseFloat(ligne.prix_achat_ht) || 0;
  const { margeFinalePct, justification } = calculMargeIntelligente(ligne);
  const margePctOverride = ligne.marge_percent !== "" && ligne.marge_percent != null ? parseFloat(ligne.marge_percent) : null;
  const margePct = Number.isFinite(margePctOverride) ? Math.max(MARGE_FFE_MIN_PCT, Math.min(MARGE_FFE_MAX_PCT, margePctOverride)) : margeFinalePct;
  const margeDH = Math.round((margePct / 100) * prixHT);
  const logVal = ligne.logistique_type === "fixe" ? (parseFloat(ligne.logistique_fixe) || 0) : Math.round(((parseFloat(ligne.logistique_percent) || 0) / 100) * prixHT);
  const totalClient = prixHT + margeDH + logVal;
  const justifFinale = Number.isFinite(margePctOverride) ? `Marge saisie ${margePct} % (recommandée : ${margeFinalePct} %). ${justification}` : justification;
  return { prixHT, margeRecommandeePct: margeFinalePct, margePct, margeDH, logVal, totalClient, justification: justifFinale };
}

/** Tranches de surface pour tarif dégressif (surface proportionnelle). */
const TRANCHES_SURFACE = [
  { max: 50, tarif_m2: 100 },
  { max: 150, tarif_m2: 80 },
  { max: Infinity, tarif_m2: 60 },
];

/** Tranches dégressives APD technique (DH/m² selon surface). */
const TRANCHES_APD_TECHNIQUE = [
  { max: 120, tarif_m2: 90, label: "< 120 m²" },
  { max: 300, tarif_m2: 70, label: "120 à 300 m²" },
  { max: Infinity, tarif_m2: 60, label: "> 300 m²" },
];

/** Tranches dégressives Dossier technique complet (DH/m² selon surface). */
const TRANCHES_DOSSIER_TECHNIQUE = [
  { max: 120, tarif_m2: 110, label: "< 120 m²" },
  { max: 300, tarif_m2: 90, label: "120 à 300 m²" },
  { max: Infinity, tarif_m2: 75, label: "> 300 m²" },
];

/** Retourne tarif au m², libellé tranche et total pour une surface et une liste de tranches. */
function getTarifDegressifParTranche(surface, tranches) {
  if (!surface || surface <= 0) return null;
  const t = tranches.find((tr) => surface <= tr.max);
  if (!t) return null;
  const total = Math.round(surface * t.tarif_m2);
  return { tarif_m2: t.tarif_m2, tranche_label: t.label, total };
}

function calculConceptionDegressif(surface) {
  if (!surface || surface <= 0) return { total: 0, base: 0, prix_unitaire: 0 };
  let total = 0;
  let prevMax = 0;
  for (const t of TRANCHES_SURFACE) {
    const slice = Math.max(0, Math.min(surface, t.max) - prevMax);
    if (slice > 0) total += Math.round(slice * t.tarif_m2);
    prevMax = t.max;
    if (surface <= t.max) break;
  }
  return { total, base: surface, prix_unitaire: surface > 0 ? Math.round(total / surface) : 0 };
}

/** Modules de prestations */
const PRESTATIONS_MODULES = [
  { id: "conseil_conception", label: "Conseil / Conception" },
  { id: "technique", label: "Technique" },
  { id: "administratif", label: "Administratif" },
  { id: "decoration_ffee", label: "Décoration / FF&E" },
  { id: "suivi_chantier", label: "Suivi chantier" },
  { id: "exterieur", label: "Extérieur" },
];

/** condition_activation: always | surface | travaux | travaux_renovation | ffee | exterieur */
const PRESTATIONS_BASE = [
  { id: "visite_conseil", label: "Visite conseil", module: "conseil_conception", type_calcul: "forfait", tarif: 2500, condition_activation: "always" },
  { id: "conception_architecturale", label: "Conception architecturale", module: "conseil_conception", type_calcul: "m2_degressif", tarif: null, condition_activation: "surface" },
  { id: "moodboard", label: "Moodboard", module: "conseil_conception", type_calcul: "forfait", tarif: 3000, condition_activation: "always" },
  { id: "plans_2d", label: "Plans 2D", module: "conseil_conception", type_calcul: "forfait", tarif: 2500, condition_activation: "surface" },
  { id: "rendus_3d", label: "Rendus 3D", module: "conseil_conception", type_calcul: "piece", tarif: 2500, condition_activation: "always" },
  { id: "shopping_list", label: "Shopping list", module: "conseil_conception", type_calcul: "forfait", tarif: 2500, condition_activation: "always" },
  { id: "cuisine_dressing", label: "Cuisine ou dressing", module: "conseil_conception", type_calcul: "forfait", tarif: 5000, condition_activation: "always" },
  { id: "apd_technique", label: "APD technique", module: "technique", type_calcul: "m2_degressif_technique", tarif: null, condition_activation: "surface" },
  { id: "dossier_technique", label: "Dossier technique complet", module: "technique", type_calcul: "m2_degressif_technique", tarif: null, condition_activation: "surface" },
  { id: "cctp", label: "CCTP", module: "technique", type_calcul: "forfait", tarif: 4000, condition_activation: "surface" },
  { id: "metrage_descriptifs", label: "Métrage et descriptifs", module: "technique", type_calcul: "forfait", tarif: 3000, condition_activation: "surface" },
  { id: "dossier_administratif", label: "Dossier administratif", module: "administratif", type_calcul: "forfait", tarif: 8000, condition_activation: "always" },
  { id: "permis_construction", label: "Permis de construction", module: "administratif", type_calcul: "forfait", tarif: 2000, condition_activation: "always" },
  { id: "gestion_achats", label: "Gestion achats", module: "decoration_ffee", type_calcul: "pct", tarif: 10, condition_activation: "ffee" },
  { id: "styling_final", label: "Styling final", module: "decoration_ffee", type_calcul: "forfait", tarif: 8000, condition_activation: "ffee" },
  { id: "suivi_chantier", label: "Suivi de chantier", module: "suivi_chantier", type_calcul: "pct", tarif: 10, condition_activation: "travaux" },
  { id: "suivi_renovation", label: "Suivi rénovation", module: "suivi_chantier", type_calcul: "pct", tarif: 9, condition_activation: "travaux_renovation" },
  { id: "paysagisme", label: "Paysagisme", module: "exterieur", type_calcul: "forfait", tarif: 10000, condition_activation: "exterieur" },
  { id: "piscine_design", label: "Piscine design", module: "exterieur", type_calcul: "forfait", tarif: 7500, condition_activation: "exterieur" },
  { id: "eclairage_exterieur", label: "Éclairage extérieur", module: "exterieur", type_calcul: "forfait", tarif: 4000, condition_activation: "exterieur" },
];

/** Suivi rénovation et suivi chantier jamais cochés par défaut. */
const DEFAULT_PRESTATIONS_ACTIVES = { conception_architecturale: true, rendus_3d: true, dossier_technique: true, dossier_administratif: true, suivi_chantier: false, suivi_renovation: false };

function isConditionFulfilled(p, form) {
  const surface = parseFloat(form.surface_interieure ?? form.surface) || 0;
  const budgetTravaux = parseFloat(form.budget_estimatif_travaux ?? form.budget_travaux) || 0;
  const budgetAchats = parseFloat(form.budget_achats) || 0;
  const travauxRenovation = form.travaux_renovation === true || form.travaux_renovation === "true";
  const surfaceJardin = parseFloat(form.surface_terrain) || 0;
  const hasExterieur = surfaceJardin > 0 || (form.dimensions_piscine || "").trim() !== "";
  const hasFfee = budgetAchats > 0 || (form.lignes_ffee || []).length > 0;
  switch (p.condition_activation) {
    case "surface": return surface > 0;
    case "travaux": return budgetTravaux > 0;
    case "travaux_renovation": return travauxRenovation && budgetTravaux > 0;
    case "ffee": return hasFfee;
    case "exterieur": return hasExterieur;
    default: return true;
  }
}

function computePrestationRow(p, form) {
  const surface = parseFloat(form.surface_interieure ?? form.surface) || 0;
  const nbPieces = parseInt(form.nombre_pieces ?? form.nb_pieces, 10) || 1;
  const budgetTravaux = parseFloat(form.budget_estimatif_travaux ?? form.budget_travaux) || 0;
  const budgetAchats = parseFloat(form.budget_achats) || 0;
  const surfaceJardin = parseFloat(form.surface_terrain) || 0;
  const piscineComplexe = form.piscine_complexe === true || form.piscine_complexe === "true";
  const moduleLabel = PRESTATIONS_MODULES.find((m) => m.id === p.module)?.label ?? "Honoraires";

  if (p.type_calcul === "m2_degressif" && surface > 0) {
    const { total, base, prix_unitaire } = calculConceptionDegressif(surface);
    return { poste: moduleLabel, detail: p.label, quantite_base: base, prix_unitaire, total_ht: total, zone: "honoraires" };
  }
  if (p.id === "apd_technique" && surface > 0) {
    const res = getTarifDegressifParTranche(surface, TRANCHES_APD_TECHNIQUE);
    if (!res) return null;
    return {
      poste: moduleLabel,
      detail: p.label,
      quantite_base: surface,
      prix_unitaire: res.tarif_m2,
      total_ht: res.total,
      zone: "honoraires",
      base_calcul: `${surface} m² — Tranche ${res.tranche_label}`,
      unite: "DH/m²",
    };
  }
  if (p.id === "dossier_technique" && surface > 0) {
    const res = getTarifDegressifParTranche(surface, TRANCHES_DOSSIER_TECHNIQUE);
    if (!res) return null;
    return {
      poste: moduleLabel,
      detail: p.label,
      quantite_base: surface,
      prix_unitaire: res.tarif_m2,
      total_ht: res.total,
      zone: "honoraires",
      base_calcul: `${surface} m² — Tranche ${res.tranche_label}`,
      unite: "DH/m²",
    };
  }
  if (p.type_calcul === "m2" && surface > 0) {
    const total = Math.round((p.tarif || 0) * surface);
    return { poste: moduleLabel, detail: p.label, quantite_base: surface, prix_unitaire: p.tarif, total_ht: total, zone: "honoraires" };
  }
  if (p.type_calcul === "piece") {
    const total = (p.tarif || 0) * nbPieces;
    return { poste: moduleLabel, detail: p.label, quantite_base: nbPieces, prix_unitaire: p.tarif, total_ht: total, zone: "honoraires" };
  }
  if (p.type_calcul === "forfait" && p.tarif != null) {
    let montant = p.tarif;
    if (p.id === "piscine_design" && piscineComplexe) montant = Math.round(montant * 1.2);
    if (p.id === "paysagisme") {
      if (surfaceJardin <= 0) return null;
      if (surfaceJardin >= 1000) montant = Math.round(20 * surfaceJardin);
      else montant = 10000;
    }
    return { poste: moduleLabel, detail: p.label, quantite_base: 1, prix_unitaire: montant, total_ht: montant, zone: "honoraires" };
  }
  if (p.type_calcul === "pct") {
    if (p.condition_activation === "travaux" || p.condition_activation === "travaux_renovation") {
      if (budgetTravaux <= 0) return null;
      const total = Math.round(((p.tarif || 0) / 100) * budgetTravaux);
      return { poste: moduleLabel, detail: p.label, quantite_base: budgetTravaux, prix_unitaire: p.tarif + " %", total_ht: total, zone: "honoraires" };
    }
    if (p.condition_activation === "ffee" && budgetAchats > 0) {
      const total = Math.round(((p.tarif || 0) / 100) * budgetAchats);
      return { poste: moduleLabel, detail: p.label, quantite_base: budgetAchats, prix_unitaire: p.tarif + " %", total_ht: total, zone: "honoraires" };
    }
    return null;
  }
  return null;
}

/** Construit les lignes du devis : uniquement prestations activées et dont la condition est remplie. */
function buildDevisSynthèse(form) {
  const budgetTravaux = parseFloat(form.budget_estimatif_travaux ?? form.budget_travaux) || 0;
  const travauxRenovation = form.travaux_renovation === true || form.travaux_renovation === "true";
  const actives = { ...DEFAULT_PRESTATIONS_ACTIVES, ...(form.prestations_actives || form.prestations_honoraires || {}) };

  const rows = [];
  let totalHonoraires = 0;

  const showBudgetTravaux = actives.suivi_chantier || (travauxRenovation && actives.suivi_renovation);
  const budgetTravauxUtilise = showBudgetTravaux && budgetTravaux > 0;
  const showBudgetAchats = actives.gestion_achats || (form.lignes_ffee || []).length > 0;

  PRESTATIONS_BASE.forEach((p) => {
    if (!actives[p.id]) return;
    if (!isConditionFulfilled(p, form)) return;
    const row = computePrestationRow(p, form);
    if (row) {
      rows.push(row);
      totalHonoraires += row.total_ht;
    }
  });

  const lignesFFE = form.lignes_ffee || [];
  let totalFFE = 0;
  lignesFFE.forEach((l) => {
    const computed = computeLigneFFE(l);
    totalFFE += computed.totalClient;
    rows.push({ poste: "FF&E", detail: (l.article || "Article") + (l.piece ? ` — ${l.piece}` : ""), quantite_base: 1, prix_unitaire: computed.totalClient, total_ht: computed.totalClient, zone: "ffee" });
  });

  const fraisDeplacement = parseFloat(form.frais_deplacement) || 0;
  const impressionsMaquettes = parseFloat(form.impressions_maquettes) || 0;
  const provisionImprevus = budgetTravauxUtilise
    ? (form.provision_imprevus !== "" && form.provision_imprevus != null ? parseFloat(form.provision_imprevus) : Math.round((DEVIS.provision_imprevus_pct_default / 100) * budgetTravaux))
    : 0;
  let totalFraisAnnexes = fraisDeplacement + impressionsMaquettes + provisionImprevus;
  if (fraisDeplacement) rows.push({ poste: "Frais annexes", detail: "Frais de déplacement", quantite_base: 1, prix_unitaire: fraisDeplacement, total_ht: fraisDeplacement, zone: "frais" });
  if (impressionsMaquettes) rows.push({ poste: "Frais annexes", detail: "Impressions / maquettes", quantite_base: 1, prix_unitaire: impressionsMaquettes, total_ht: impressionsMaquettes, zone: "frais" });
  if (provisionImprevus) rows.push({ poste: "Frais annexes", detail: "Provision imprévus (5 % budget travaux)", quantite_base: 1, prix_unitaire: provisionImprevus, total_ht: provisionImprevus, zone: "frais" });

  const totalGeneralHT = totalHonoraires + totalFFE + totalFraisAnnexes;
  return { rows, totalHonoraires, totalFFE, totalFraisAnnexes, totalGeneralHT, showBudgetTravaux, showBudgetAchats: showBudgetAchats || lignesFFE.length > 0 };
}

const MISSION_TYPES = [
  { id: "conception", label: "Conception" },
  { id: "conception_technique", label: "Conception + Technique" },
  { id: "mission_complete", label: "Mission complète" },
];

const PAYS_OPTIONS = [
  { id: "France", label: "France" },
  { id: "Maroc", label: "Maroc" },
  { id: "Suisse", label: "Suisse" },
  { id: "UAE", label: "UAE" },
  { id: "Autre", label: "Autre" },
];

const STATUTS = [
  { id: "brouillon", label: "Brouillon" },
  { id: "envoye", label: "Envoyé" },
  { id: "signe", label: "Signé" },
];

const DEFAULT_FORM = {
  client_name: "",
  project_name: "",
  mission_type: "conception",
  pays: "France",
  surface: "",
  surface_interieure: "",
  nombre_niveaux: "",
  nombre_pieces: "1",
  nb_pieces: "1",
  nb_etages: "0",
  surface_terrain: "",
  dimensions_piscine: "",
  travaux_renovation: false,
  prestations_actives: {},
  prestations_honoraires: {},
  budget_estimatif_travaux: "",
  budget_travaux: "",
  budget_achats: "",
  piscine_complexe: false,
  complexite: "moyenne",
  montant: "",
  lignes_ffee: [],
  frais_deplacement: "",
  impressions_maquettes: "",
  provision_imprevus: "",
  acompte_pct: "30",
  echeancier_acompte: "Acompte",
  echeancier_lancement: "Lancement",
  echeancier_fin_projet: "Fin projet",
  statut: "brouillon",
};

export default function PropositionContractuellePage({ projectId, setActiveTab, projectData = {}, cabinetId = null }) {
  const [project, setProject] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialFormHydrated, setInitialFormHydrated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);

  const [sourcingForm, setSourcingForm] = useState({ query: "", url: "", imageName: "" });
  const [sourcingResults, setSourcingResults] = useState([]);
  const [sourcingLoading, setSourcingLoading] = useState(false);
  const [sourcingError, setSourcingError] = useState("");
  const [ffeLineSourcing, setFfeLineSourcing] = useState({});
  const [ffeZoneFilter, setFfeZoneFilter] = useState("");

  const deviseCode = projectData.deviseCode || "MAD";
  const deviseLabel = deviseCode === "MAD" ? "DH" : deviseCode;

  useEffect(() => {
    if (!projectId || cabinetId == null) return;
    const STORAGE_KEY = `yoma_proposition_contractuelle_form_v1_${projectId}`;
    let localDraft = null;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) localDraft = JSON.parse(raw);
    } catch (_) {}
    let cancelled = false;
    setLoading(true);
    setInitialFormHydrated(false);
    Promise.all([
      supabase.from("projects").select("*").eq("id", projectId).eq("cabinet_id", cabinetId).single(),
      supabase
        .from("contract_proposals")
        .select("*")
        .eq("project_id", projectId)
        .eq("cabinet_id", cabinetId)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]).then(([pRes, propRes]) => {
      if (cancelled) return;
      if (pRes.data) {
        setProject(pRes.data);
        setForm((f) => ({
          ...f,
          client_name: pRes.data.client_name ?? DEFAULT_FORM.client_name,
          project_name: pRes.data.name ?? DEFAULT_FORM.project_name,
        }));
      }
      if (propRes.data) {
        setProposal(propRes.data);
        const storedForm = propRes.data.form_state || {};
        setForm(() => ({
          ...DEFAULT_FORM,
          ...storedForm,
          client_name:
            propRes.data.client_name ??
            storedForm.client_name ??
            DEFAULT_FORM.client_name,
          project_name:
            propRes.data.project_name ??
            storedForm.project_name ??
            DEFAULT_FORM.project_name,
          mission_type:
            propRes.data.mission_type === "conception_suivi"
              ? "conception_technique"
              : propRes.data.mission_type ??
                storedForm.mission_type ??
                DEFAULT_FORM.mission_type,
          pays: propRes.data.pays ?? storedForm.pays ?? DEFAULT_FORM.pays,
          surface: propRes.data.surface_m2 != null
            ? String(propRes.data.surface_m2)
            : storedForm.surface ?? DEFAULT_FORM.surface,
          surface_interieure: propRes.data.surface_m2 != null
            ? String(propRes.data.surface_m2)
            : storedForm.surface_interieure ??
              storedForm.surface ??
              DEFAULT_FORM.surface_interieure,
          nombre_niveaux:
            storedForm.nombre_niveaux ?? DEFAULT_FORM.nombre_niveaux,
          nombre_pieces:
            storedForm.nombre_pieces ?? DEFAULT_FORM.nombre_pieces,
          nb_pieces: storedForm.nb_pieces ?? DEFAULT_FORM.nb_pieces,
          nb_etages: storedForm.nb_etages ?? DEFAULT_FORM.nb_etages,
          surface_terrain:
            storedForm.surface_terrain ?? DEFAULT_FORM.surface_terrain,
          dimensions_piscine:
            storedForm.dimensions_piscine ?? DEFAULT_FORM.dimensions_piscine,
          travaux_renovation:
            storedForm.travaux_renovation ??
            DEFAULT_FORM.travaux_renovation,
          budget_estimatif_travaux:
            storedForm.budget_estimatif_travaux ??
            DEFAULT_FORM.budget_estimatif_travaux,
          budget_travaux:
            storedForm.budget_travaux ?? DEFAULT_FORM.budget_travaux,
          budget_achats:
            storedForm.budget_achats ?? DEFAULT_FORM.budget_achats,
          piscine_complexe:
            storedForm.piscine_complexe ?? DEFAULT_FORM.piscine_complexe,
          prestations_actives:
            storedForm.prestations_actives ??
            DEFAULT_FORM.prestations_actives,
          prestations_honoraires:
            storedForm.prestations_honoraires ??
            DEFAULT_FORM.prestations_honoraires,
          complexite:
            propRes.data.complexite ??
            storedForm.complexite ??
            DEFAULT_FORM.complexite,
          montant:
            propRes.data.montant != null
              ? String(propRes.data.montant)
              : storedForm.montant != null
              ? String(storedForm.montant)
              : DEFAULT_FORM.montant,
          acompte_pct:
            propRes.data.acompte_pct != null
              ? String(propRes.data.acompte_pct)
              : storedForm.acompte_pct ??
                DEFAULT_FORM.acompte_pct,
          echeancier_acompte:
            propRes.data.echeancier_acompte ??
            storedForm.echeancier_acompte ??
            DEFAULT_FORM.echeancier_acompte,
          echeancier_lancement:
            propRes.data.echeancier_lancement ??
            storedForm.echeancier_lancement ??
            DEFAULT_FORM.echeancier_lancement,
          echeancier_fin_projet:
            propRes.data.echeancier_fin_projet ??
            storedForm.echeancier_fin_projet ??
            DEFAULT_FORM.echeancier_fin_projet,
          statut:
            propRes.data.statut ??
            storedForm.statut ??
            DEFAULT_FORM.statut,
          lignes_ffee:
            storedForm.lignes_ffee ??
            DEFAULT_FORM.lignes_ffee,
          frais_deplacement:
            storedForm.frais_deplacement ??
            DEFAULT_FORM.frais_deplacement,
          impressions_maquettes:
            storedForm.impressions_maquettes ??
            DEFAULT_FORM.impressions_maquettes,
          provision_imprevus:
            storedForm.provision_imprevus ??
            DEFAULT_FORM.provision_imprevus,
        }));
      } else if (localDraft) {
        setForm(() => ({
          ...DEFAULT_FORM,
          ...localDraft,
          client_name: localDraft.client_name ?? (pRes.data?.client_name ?? DEFAULT_FORM.client_name),
          project_name: localDraft.project_name ?? (pRes.data?.name ?? DEFAULT_FORM.project_name),
        }));
      }
    }).finally(() => {
      if (!cancelled) {
        setLoading(false);
        setInitialFormHydrated(true);
      }
    });
    return () => { cancelled = true; };
  }, [projectId, cabinetId]);

  // Persistance locale minimale (brouillon de travail)
  useEffect(() => {
    if (!projectId || !initialFormHydrated) return;
    const STORAGE_KEY = `yoma_proposition_contractuelle_form_v1_${projectId}`;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
    } catch (_) {}
  }, [projectId, initialFormHydrated, form]);

  const devisSynthèse = useMemo(() => buildDevisSynthèse(form), [form]);
  const activesPrestations = useMemo(() => ({ ...DEFAULT_PRESTATIONS_ACTIVES, ...(form.prestations_actives || form.prestations_honoraires || {}) }), [form.prestations_actives, form.prestations_honoraires]);
  const showBudgetTravaux = activesPrestations.suivi_chantier || (form.travaux_renovation && activesPrestations.suivi_renovation);
  const showBudgetAchats = (form.lignes_ffee || []).length > 0 || activesPrestations.gestion_achats;

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function setPrestationActive(id, checked) {
    setForm((f) => ({ ...f, prestations_actives: { ...(f.prestations_actives || {}), [id]: checked } }));
  }

  function isPrestationVisible(p) {
    if (p.condition_activation === "travaux_renovation" && !form.travaux_renovation) return false;
    return true;
  }

  function addLigneFFE() {
    setForm((f) => ({
      ...f,
      lignes_ffee: [
        ...(f.lignes_ffee || []),
        {
          id: crypto.randomUUID?.() ?? Date.now(),
          article: "",
          piece: "",
          marque: "",
          reference: "",
          quantite: "",
          prix_achat_ht: "",
          prix_public: "",
          type_produit: "",
          provenance_produit: "",
          qualite_produit: "",
          authenticite_produit: "",
          marge_percent: "",
          logistique_type: "percent",
          logistique_percent: "",
          logistique_fixe: "",
          fournisseur: "",
          image_url: "",
          source_url: "",
          statut_ffee: "selection",
        },
      ],
    }));
  }

  function updateLigneFFE(index, field, value) {
    setForm((f) => {
      const lignes = [...(f.lignes_ffee || [])];
      if (!lignes[index]) return f;
      lignes[index] = { ...lignes[index], [field]: value };
      return { ...f, lignes_ffee: lignes };
    });
  }

  function removeLigneFFE(index) {
    setForm((f) => ({ ...f, lignes_ffee: (f.lignes_ffee || []).filter((_, i) => i !== index) }));
  }

  function appliquerTotalDevis() {
    setForm((f) => ({ ...f, montant: String(devisSynthèse.totalGeneralHT) }));
  }

  function analyserSourcing() {
    const baseQuery = (sourcingForm.query || sourcingForm.url || "Produit FF&E").trim();
    if (!baseQuery) {
      setSourcingError("Saisissez une recherche ou une URL produit.");
      return;
    }
    setSourcingLoading(true);
    setSourcingError("");
    try {
      const now = new Date();
      const devise = "MAD";
      const prixBase = 2500;
      const produits = [
        {
          id: "1",
          image: "https://via.placeholder.com/120x120?text=FF%26E",
          nom: baseQuery,
          marchand: "À qualifier",
          prix: prixBase,
          devise,
          disponibilite: "À vérifier",
          lien: sourcingForm.url || "https://example.com/produit",
          categorie: "mobilier",
          confiance: "70 %",
          releveLe: now.toLocaleString("fr-FR"),
        },
      ];
      setSourcingResults(produits);
    } catch (e) {
      setSourcingError("Impossible d'analyser le produit. Réessayez.");
    } finally {
      setSourcingLoading(false);
    }
  }

  function ajouterResultatCommeLigne(result) {
    setForm((f) => ({
      ...f,
      lignes_ffee: [
        ...(f.lignes_ffee || []),
        {
          id: crypto.randomUUID?.() ?? Date.now(),
          article: result.nom,
          piece: "",
          marque: result.marque || "",
          reference: result.reference || "",
          quantite: "",
          prix_achat_ht: String(result.prix),
          prix_public: String(result.prix),
          type_produit: "",
          provenance_produit: "",
          qualite_produit: "",
          authenticite_produit: "",
          marge_percent: "",
          logistique_type: "percent",
          logistique_percent: "",
          logistique_fixe: "",
          fournisseur: result.marchand,
          image_url: result.image,
          source_url: result.lien,
          statut_ffee: "selection",
        },
      ],
    }));
  }

  function rechercherProduitPourLigne(ligne, index) {
    const parts = [
      ligne.article || "",
      ligne.marque || "",
      ligne.reference || "",
    ]
      .map((s) => (s || "").trim())
      .filter(Boolean);
    const key = ligne.id || index;
    const clarification = (ffeLineSourcing[key]?.clarification || "").trim();
    const query = [...parts, clarification].join(" ").trim();
    if (!query) {
      setFfeLineSourcing((prev) => ({
        ...prev,
        [key]: {
          loading: false,
          results: [],
          error: "Saisir au moins un nom / marque de produit pour lancer la recherche.",
          status: "idle",
        },
      }));
      return;
    }
    setFfeLineSourcing((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        loading: true,
        results: [],
        error: null,
        status: "searching",
      },
    }));
    const now = new Date();

    // V1 : si aucune précision, on simule un résultat ambigu (2 références proches)
    if (!clarification) {
      const results = [
        {
          id: `${key}-r1`,
          image: "https://via.placeholder.com/80x80?text=FF%26E+1",
          nom: ligne.article || query,
          marque: ligne.marque || "Marque A",
          reference: ligne.reference || "REF-001",
          marchand: "Fournisseur A",
          prix: 2500,
          devise: "MAD",
          disponibilite: "À vérifier",
          lien: "https://example.com/produit-a",
          categorie: "mobilier",
          releveLe: now.toLocaleString("fr-FR"),
          confiance: "moyenne",
        },
        {
          id: `${key}-r2`,
          image: "https://via.placeholder.com/80x80?text=FF%26E+2",
          nom: `${ligne.article || query} (variante)`,
          marque: ligne.marque || "Marque A",
          reference: "REF-002",
          marchand: "Fournisseur B",
          prix: 2600,
          devise: "MAD",
          disponibilite: "À vérifier",
          lien: "https://example.com/produit-b",
          categorie: "mobilier",
          releveLe: now.toLocaleString("fr-FR"),
          confiance: "faible",
        },
      ];
      setFfeLineSourcing((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          loading: false,
          results,
          error: null,
          status: "ambiguous",
        },
      }));
      return;
    }

    // Si des précisions ont été données, on simule un match unique à confiance élevée
    const result = {
      id: `${key}-r-main`,
      image: "https://via.placeholder.com/80x80?text=FF%26E",
      nom: ligne.article || query,
      marque: ligne.marque || "Marque confirmée",
      reference: ligne.reference || "REF-PRINCIPALE",
      marchand: "Fournisseur confirmé",
      prix: 2550,
      devise: "MAD",
      disponibilite: "À vérifier",
      lien: "https://example.com/produit",
      categorie: "mobilier",
      releveLe: now.toLocaleString("fr-FR"),
      confiance: "élevée",
    };

    setFfeLineSourcing((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        loading: false,
        results: [result],
        error: null,
        status: "resolved",
      },
    }));

    // Auto-remplissage uniquement si un produit clair et confiance élevée
    appliquerProduitALigne(ligne, index, result);
  }

  function appliquerProduitALigne(ligne, index, produit) {
    const key = ligne.id || index;
    setForm((f) => {
      const lignes = [...(f.lignes_ffee || [])];
      if (!lignes[index]) return f;
      lignes[index] = {
        ...lignes[index],
        article: produit.nom,
        marque: produit.marque || lignes[index].marque || "",
        reference: produit.reference || lignes[index].reference || "",
        prix_achat_ht: String(produit.prix),
        prix_public: String(produit.prix),
        fournisseur: produit.marchand,
        image_url: produit.image,
        source_url: produit.lien,
        statut_ffee: "selection",
      };
      return { ...f, lignes_ffee: lignes };
    });
    setFfeLineSourcing((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), selectedId: produit.id },
    }));
  }

  function genererPdf() {
    const doc = new jsPDF({
      unit: "pt",
      format: "a4",
    });

    const marginX = 56;
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 160;
    const logoHeight = 55;
    const logoX = (pageWidth - logoWidth) / 2;
    let y = 64;

    const dateStr = new Date().toLocaleDateString("fr-FR");
    const clientName = form.client_name || project?.client_name || "";
    const projectName = form.project_name || project?.name || "";
    const nomEmetteur = projectData.emetteur_nom || projectData.companyName || "YOMA DESIGN";

    // En-tête : logo (companyLogo prioritaire, sinon SVG) ou fallback texte
    let logoDrawn = false;
    if (projectData.companyLogo) {
      try {
        const fmt = projectData.companyLogo.indexOf("image/png") !== -1 ? "PNG" : "JPEG";
        doc.addImage(projectData.companyLogo, fmt, logoX, y - 10, logoWidth, logoHeight);
        logoDrawn = true;
      } catch (e) {}
    }
    if (!logoDrawn) {
      try {
        doc.addImage(YOMA_LOGO_SVG, "SVG", logoX, y - 10, logoWidth, logoHeight);
        logoDrawn = true;
      } catch (e) {}
    }
    if (!logoDrawn) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text(nomEmetteur, marginX, y);
    }
    doc.setDrawColor(201, 169, 110);
    doc.setLineWidth(1);
    doc.line(marginX, y + logoHeight - 3, marginX + 120, y + logoHeight - 3);
    y += logoHeight + 25;

    doc.setFontSize(20);
    doc.text("Proposition contractuelle", marginX, y);
    y += 22;

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(`Date : ${dateStr}`, marginX, y);
    y += 28;

    const addSectionTitle = (title) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text(title, marginX, y);
      y += 8;
      doc.setDrawColor(231, 225, 216);
      doc.setLineWidth(0.7);
      doc.line(marginX, y, 539, y);
      y += 14;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
    };

    const addField = (label, value) => {
      if (y > 770) {
        doc.addPage();
        y = 64;
      }
      doc.setFont("helvetica", "bold");
      doc.text(`${label} :`, marginX, y);
      doc.setFont("helvetica", "normal");
      doc.text(String(value || "—"), marginX + 130, y);
      y += 18;
    };

    // 1. Informations client
    addSectionTitle("1. Informations client");
    addField("Client", clientName);
    addField("Projet", projectName);
    y += 6;

    // 2. Informations mission
    const missionLabel =
      MISSION_TYPES.find((m) => m.id === form.mission_type)?.label || form.mission_type || "—";
    addSectionTitle("2. Informations mission");
    addField("Type de mission", missionLabel);
    addField("Pays", form.pays || "—");
    addField("Surface (m²)", form.surface_interieure || form.surface || "—");
    addField("Complexité", form.complexite || "—");
    y += 6;

    // 3. Détail financier
    const statutLabel =
      STATUTS.find((s) => s.id === form.statut)?.label || form.statut || "—";
    addSectionTitle("3. Détail financier");
    addField("Montant", form.montant ? fmtMontant(form.montant, deviseLabel) : fmtMontant(devisSynthèse.totalGeneralHT, deviseLabel));
    addField("Acompte (%)", form.acompte_pct || "—");
    addField("Échéancier - acompte", form.echeancier_acompte || "—");
    addField("Échéancier - lancement", form.echeancier_lancement || "—");
    addField("Échéancier - fin de projet", form.echeancier_fin_projet || "—");
    addField("Statut", statutLabel);

    // Pied de page
    const footerY = 812;
    doc.setDrawColor(231, 225, 216);
    doc.setLineWidth(0.5);
    doc.line(marginX, footerY - 24, 539, footerY - 24);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    const hasContact = projectData.emetteur_email || projectData.emetteur_telephone || projectData.emetteur_adresse;
    if (hasContact) {
      const contactParts = [projectData.emetteur_adresse, projectData.emetteur_email, projectData.emetteur_telephone].filter(Boolean);
      doc.text(contactParts.join(" • "), marginX, footerY - 22);
    }
    doc.text(`${nomEmetteur} — Proposition contractuelle`, marginX, footerY - 10);

    const safeClient = clientName || "client";
    const safeProject = projectName || "projet";
    const filename = `Proposition_${safeClient}_${safeProject}.pdf`.replace(/[\\/:*?"<>|]+/g, "_");
    doc.save(filename);
  }

  async function enregistrer() {
    if (!projectId || cabinetId == null) return;
    setSaveMessage(null);
    setSaving(true);
    const payload = {
      project_id: projectId,
      client_name: form.client_name.trim() || project?.client_name || "",
      project_name: form.project_name.trim() || project?.name || "",
      mission_type: form.mission_type,
      pays: form.pays || null,
      surface_m2: form.surface_interieure ? parseFloat(form.surface_interieure) : (form.surface ? parseFloat(form.surface) : null),
      complexite: form.complexite || null,
      montant: form.montant ? parseFloat(form.montant) : null,
      acompte_pct: form.acompte_pct ? parseFloat(form.acompte_pct) : null,
      echeancier_acompte: form.echeancier_acompte?.trim() || null,
      echeancier_lancement: form.echeancier_lancement?.trim() || null,
      echeancier_fin_projet: form.echeancier_fin_projet?.trim() || null,
      statut: form.statut,
      cabinet_id: cabinetId,
      updated_at: new Date().toISOString(),
      form_state: form,
    };
    const { data, error } = await supabase
      .from("contract_proposals")
      .upsert(payload, { onConflict: "project_id" })
      .eq("cabinet_id", cabinetId)
      .select()
      .single();
    if (!error && data) {
      setProposal(data);
      setSaveMessage({ type: "success", text: "Proposition enregistrée avec succès" });
      setTimeout(() => setSaveMessage(null), 4000);
    } else if (error) {
      setSaveMessage({ type: "error", text: error.message || "Erreur lors de l'enregistrement." });
    }
    setSaving(false);
  }

  function importerFfeDepuisCsv(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      alert("Veuillez sélectionner un fichier CSV.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = String(e.target?.result || "");
      const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
      if (lines.length < 2) {
        alert("Le fichier CSV ne contient aucune ligne de produit.");
        return;
      }
      const headers = lines[0].split(";").length > 1 ? lines[0].split(";") : lines[0].split(",");
      const normalize = (s) => s.trim().toLowerCase().replace(/\s+/g, "_");
      const headerMap = {};
      headers.forEach((h, idx) => {
        headerMap[normalize(h)] = idx;
      });

      const required = ["designation", "prix_achat_ht"];
      const missing = required.filter((k) => headerMap[k] == null);
      if (missing.length > 0) {
        alert("Colonnes CSV manquantes : " + missing.join(", "));
        return;
      }

      const newLines = [];
      for (let i = 1; i < lines.length; i += 1) {
        const row = lines[i].trim();
        if (!row) continue;
        const cols = row.split(";").length > 1 ? row.split(";") : row.split(",");
        const get = (key, fallback = "") => {
          const idx = headerMap[key];
          return idx != null ? (cols[idx] || "").trim() : fallback;
        };
        const prixAchat = parseFloat(get("prix_achat_ht", "0").replace(",", ".")) || 0;
        const quantite = parseFloat(get("quantite", "1").replace(",", ".")) || 1;
        newLines.push({
          id: crypto.randomUUID?.() ?? Date.now() + i,
          article: get("designation"),
          piece: get("piece"),
          zone: get("zone"),
          marque: get("marque"),
          reference: get("reference"),
          quantite: String(quantite),
          prix_achat_ht: String(prixAchat),
          prix_public: "",
          type_produit: "",
          provenance_produit: "",
          qualite_produit: "",
          authenticite_produit: "",
          marge_percent: "",
          logistique_type: "percent",
          logistique_percent: "",
          logistique_fixe: "",
          fournisseur: get("fournisseur"),
          image_url: get("image_url"),
          source_url: get("lien_produit"),
          statut_ffee: "selection",
          categorie: get("categorie"),
        });
      }
      if (newLines.length === 0) {
        alert("Aucune ligne FF&E valide trouvée dans le CSV.");
        return;
      }
      setForm((f) => ({
        ...f,
        lignes_ffee: [...(f.lignes_ffee || []), ...newLines],
      }));
      event.target.value = "";
    };
    reader.onerror = () => {
      alert("Erreur lors de la lecture du fichier CSV.");
    };
    reader.readAsText(file);
  }

  if (!projectId) {
    return (
      <div style={{ padding: 48, textAlign: "center", color: INK_MUTED }}>Aucun projet sélectionné.</div>
    );
  }
  if (cabinetId == null) {
    return (
      <div style={{ padding: 48, textAlign: "center", color: INK_MUTED }}>Chargement...</div>
    );
  }
  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: "center", color: INK_MUTED }}>Chargement…</div>
    );
  }

  return (
    <div style={{ fontFamily: "system-ui, sans-serif", color: INK, maxWidth: 720, margin: "0 auto" }}><div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}><div><YomaLogo width={150} height={50} /><div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: GOLD, fontWeight: 700, margin: "8px 0 4px" }}>Proposition contractuelle</div><div style={{ fontSize: 20, fontWeight: 800, color: INK }}>{project?.name || "Projet"}</div></div>
        {setActiveTab && (
          <button onClick={() => setActiveTab("projets:" + projectId)} style={{ padding: "8px 14px", background: SOFT_BG, border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 12, fontWeight: 600, color: INK_SOFT, cursor: "pointer" }}>← Retour au projet</button>
        )}
      </div><div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "24px 28px", marginBottom: 20 }}><div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: GOLD, fontWeight: 700, marginBottom: 16 }}>1. Informations client</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}><div><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Nom client</label><input value={form.client_name} onChange={(e) => update("client_name", e.target.value)} placeholder="Nom du client" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, boxSizing: "border-box" }} /></div><div><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Nom projet</label><input value={form.project_name} onChange={(e) => update("project_name", e.target.value)} placeholder="Nom du projet" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, boxSizing: "border-box" }} /></div></div></div><div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "24px 28px", marginBottom: 20 }}><div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: GOLD, fontWeight: 700, marginBottom: 16 }}>2. Type de mission</div><select value={form.mission_type} onChange={(e) => update("mission_type", e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, background: WHITE }}>
          {MISSION_TYPES.map((m) => (<option key={m.id} value={m.id}>{m.label}</option>))}
        </select></div><div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "24px 28px", marginBottom: 20 }}><div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: GOLD, fontWeight: 700, marginBottom: 16 }}>3. Zone 1 — Caractéristiques du projet</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}><div><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Surface intérieure (m²)</label><input type="number" min="0" step="1" value={form.surface_interieure || form.surface} onChange={(e) => setForm((f) => ({ ...f, surface_interieure: e.target.value, surface: e.target.value }))} placeholder="ex. 120" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, boxSizing: "border-box" }} /></div><div><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Nombre de niveaux</label><input type="number" min="0" step="1" value={form.nombre_niveaux ?? form.nb_etages} onChange={(e) => setForm((f) => ({ ...f, nombre_niveaux: e.target.value, nb_etages: e.target.value }))} placeholder="0" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, boxSizing: "border-box" }} /></div><div><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Nombre de pièces</label><input type="number" min="1" step="1" value={form.nombre_pieces || form.nb_pieces} onChange={(e) => setForm((f) => ({ ...f, nombre_pieces: e.target.value, nb_pieces: e.target.value }))} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, boxSizing: "border-box" }} /></div><div><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Surface terrain (m²)</label><input type="number" min="0" step="1" value={form.surface_terrain} onChange={(e) => update("surface_terrain", e.target.value)} placeholder="0" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, boxSizing: "border-box" }} /></div><div><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Dimensions piscine</label><input value={form.dimensions_piscine} onChange={(e) => update("dimensions_piscine", e.target.value)} placeholder="ex. 10 × 5 m" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, boxSizing: "border-box" }} /></div><div><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Pays</label><select value={form.pays} onChange={(e) => update("pays", e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, background: WHITE }}>
              {PAYS_OPTIONS.map((p) => (<option key={p.id} value={p.id}>{p.label}</option>))}
            </select></div><div style={{ gridColumn: "1 / -1" }}><label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}><input type="checkbox" checked={!!form.travaux_renovation} onChange={(e) => update("travaux_renovation", e.target.checked)} /><span>Travaux de rénovation (oui = suivi rénovation et budget travaux possibles)</span></label></div>
          {showBudgetTravaux && (
            <div style={{ gridColumn: "1 / -1" }}><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Budget estimatif travaux (DH)</label><input type="number" min="0" value={form.budget_estimatif_travaux || form.budget_travaux} onChange={(e) => setForm((f) => ({ ...f, budget_estimatif_travaux: e.target.value, budget_travaux: e.target.value }))} placeholder="Pour suivi chantier / suivi rénovation %" style={{ width: "100%", maxWidth: 280, padding: "10px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, boxSizing: "border-box" }} /></div>
          )}
          {showBudgetAchats && (
            <div style={{ gridColumn: "1 / -1" }}><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Budget achats / FF&E (DH)</label><input type="number" min="0" value={form.budget_achats} onChange={(e) => update("budget_achats", e.target.value)} placeholder="Optionnel" style={{ width: "100%", maxWidth: 280, padding: "10px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, boxSizing: "border-box" }} /></div>
          )}
        </div></div><div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "24px 28px", marginBottom: 20 }}><div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: GOLD, fontWeight: 700, marginBottom: 16 }}>4. Zone 2 — Honoraires de services</div><p style={{ fontSize: 12, color: INK_MUTED, marginBottom: 16 }}>
          Cochez les prestations à inclure. Seules les prestations activées et dont la condition est remplie apparaissent dans le devis final. Certaines prestations dépendent de champs comme la surface, le budget travaux, le budget achats (FF&E) et les éléments d'extérieur.
        </p>
        {PRESTATIONS_MODULES.map((mod) => {
          const prestationsMod = PRESTATIONS_BASE.filter((p) => p.module === mod.id && isPrestationVisible(p));
          if (prestationsMod.length === 0) return null;
          return (
            <div key={mod.id} style={{ marginBottom: 18 }}><div style={{ fontSize: 12, fontWeight: 700, color: INK, marginBottom: 8, paddingBottom: 4, borderBottom: `1px solid ${BORDER}` }}>{mod.label}</div><div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {prestationsMod.map((p) => (
                  <label key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}><input type="checkbox" checked={!!activesPrestations[p.id]} onChange={(e) => setPrestationActive(p.id, e.target.checked)} /><span>{p.label}</span><span style={{ fontSize: 11, color: INK_MUTED }}>({p.type_calcul === "forfait" ? "forfait" : p.type_calcul === "m2" ? "m²" : p.type_calcul === "m2_degressif" || p.type_calcul === "m2_degressif_technique" ? "m² dégressif" : p.type_calcul === "piece" ? "pièce" : "%"})</span></label>
                ))}
              </div></div>
          );
        })}
      </div><div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "24px 28px", marginBottom: 20 }}><div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: GOLD, fontWeight: 700, marginBottom: 16 }}>5. Zone 3 — FF&E / Achats</div><div
          style={{
            padding: "14px 16px",
            borderRadius: 10,
            background: SOFT_BG,
            border: `1px dashed ${BORDER}`,
            marginBottom: 16,
          }}
        ><div style={{ fontSize: 12, fontWeight: 700, color: INK, marginBottom: 8 }}>
            Sourcing FF&amp;E assisté par IA (bêta)
          </div><div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 10, marginBottom: 10 }}><input
              value={sourcingForm.query}
              onChange={(e) => setSourcingForm((s) => ({ ...s, query: e.target.value }))}
              placeholder="Recherche produit (ex. Fauteuil velours vert)"
              style={{
                padding: "8px 10px",
                borderRadius: 8,
                border: `1px solid ${BORDER}`,
                fontSize: 12,
                boxSizing: "border-box",
              }}
            /><input
              value={sourcingForm.url}
              onChange={(e) => setSourcingForm((s) => ({ ...s, url: e.target.value }))}
              placeholder="URL produit (optionnel)"
              style={{
                padding: "8px 10px",
                borderRadius: 8,
                border: `1px solid ${BORDER}`,
                fontSize: 12,
                boxSizing: "border-box",
              }}
            /></div><div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}><label
              style={{
                fontSize: 11,
                color: INK_MUTED,
                display: "flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
              }}
            ><span
                style={{
                  padding: "6px 10px",
                  borderRadius: 8,
                  border: `1px solid ${BORDER}`,
                  background: WHITE,
                  fontSize: 11,
                  fontWeight: 600,
                  color: INK_SOFT,
                }}
              >
                Image produit (bientôt)
              </span></label><button
              type="button"
              onClick={analyserSourcing}
              disabled={sourcingLoading}
              style={{
                padding: "8px 16px",
                background: GOLD,
                borderRadius: 8,
                border: "none",
                fontSize: 12,
                fontWeight: 700,
                color: WHITE,
                cursor: sourcingLoading ? "not-allowed" : "pointer",
                opacity: sourcingLoading ? 0.7 : 1,
              }}
            >
              Analyser / sourcer
            </button><span style={{ fontSize: 11, color: INK_MUTED }}>
              Prix et disponibilité relevés au moment de la recherche, à revalider avant commande.
            </span>
            {sourcingError && <div style={{ fontSize: 11, color: RED, fontWeight: 700 }}>{sourcingError}</div>}
          </div>

          {sourcingResults.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
              {sourcingResults.map((r) => (
                <div
                  key={r.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr auto",
                    gap: 10,
                    padding: "10px 12px",
                    borderRadius: 10,
                    background: WHITE,
                    border: `1px solid ${BORDER}`,
                    alignItems: "center",
                  }}
                ><div
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: 8,
                      overflow: "hidden",
                      background: SOFT_BG,
                      border: `1px solid ${BORDER}`,
                    }}
                  ><img
                      src={r.image}
                      alt={r.nom}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    /></div><div style={{ fontSize: 12, color: INK_SOFT }}><div style={{ fontWeight: 700, color: INK, marginBottom: 2 }}>{r.nom}</div><div style={{ marginBottom: 2 }}><span style={{ color: INK_MUTED }}>Marchand : </span><span>{r.marchand}</span></div><div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}><span><strong>{r.prix.toLocaleString("fr-FR")} {r.devise}</strong></span><span style={{ color: INK_MUTED }}>Disponibilité : {r.disponibilite}</span><span style={{ color: INK_MUTED }}>Confiance : {r.confiance}</span></div><div style={{ marginTop: 2 }}><a
                        href={r.lien}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: 11, color: BLUE, textDecoration: "underline" }}
                      >
                        Ouvrir la fiche produit
                      </a></div></div><div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}><div
                      style={{
                        fontSize: 10,
                        padding: "2px 8px",
                        borderRadius: 999,
                        background: GOLD_BG,
                        border: `1px solid ${GOLD_PALE}`,
                        color: GOLD,
                      }}
                    >
                      Catégorie : {r.categorie}
                    </div><button
                      type="button"
                      onClick={() => ajouterResultatCommeLigne(r)}
                      style={{
                        padding: "6px 10px",
                        borderRadius: 8,
                        border: `1px solid ${GOLD}`,
                        background: GOLD_BG,
                        color: GOLD,
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      Ajouter au tableau FF&amp;E
                    </button></div></div>
              ))}
            </div>
          )}

        <p style={{ fontSize: 12, color: INK_MUTED, marginTop: 16, marginBottom: 8 }}>
          Marge recommandée selon type, prix, provenance, qualité et authenticité (base 15 %, fourchette 8–30 %). Total client = prix achat + marge + logistique.
        </p><div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}><span style={{ fontSize: 11, color: INK_MUTED }}>Filtrer par pièce / zone :</span><input
            value={ffeZoneFilter}
            onChange={(e) => setFfeZoneFilter(e.target.value)}
            placeholder="ex. Salon, Suite, Terrasse, Cuisine…"
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              border: `1px solid ${BORDER}`,
              fontSize: 12,
              boxSizing: "border-box",
              minWidth: 160,
            }}
          /></div>

        {(form.lignes_ffee || [])
          .filter((l) => {
            if (!ffeZoneFilter.trim()) return true;
            const z = (l.zone || l.piece || "").toLowerCase();
            return z.includes(ffeZoneFilter.trim().toLowerCase());
          })
          .map((ligne, index) => {
            const comp = computeLigneFFE(ligne);
            const { fourchette } = calculMargeIntelligente(ligne);
            const key = ligne.id || index;
            const sourcing = ffeLineSourcing[key] || { loading: false, results: [], error: null, selectedId: null };
            return (
              <div key={ligne.id || index} style={{ marginBottom: 16, padding: 14, background: SOFT_BG, borderRadius: 10, border: `1px solid ${BORDER}` }}><div style={{ display: "grid", gridTemplateColumns: "1.6fr 1.1fr 0.7fr 0.7fr auto", gap: 10, marginBottom: 8 }}><div style={{ display: "flex", flexDirection: "column", gap: 4 }}><input value={ligne.article || ""} onChange={(e) => updateLigneFFE(index, "article", e.target.value)} placeholder="Article" style={{ padding: "8px 10px", borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: 12, boxSizing: "border-box" }} /><button
                    type="button"
                    onClick={() => rechercherProduitPourLigne(ligne, index)}
                    style={{
                      alignSelf: "flex-start",
                      padding: "5px 8px",
                      borderRadius: 999,
                      border: `1px solid ${GOLD_PALE}`,
                      background: GOLD_BG,
                      color: GOLD,
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Rechercher avec IA
                  </button></div><input value={ligne.marque || ""} onChange={(e) => updateLigneFFE(index, "marque", e.target.value)} placeholder="Marque" style={{ padding: "8px 10px", borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: 12, boxSizing: "border-box" }} /><input value={ligne.reference || ""} onChange={(e) => updateLigneFFE(index, "reference", e.target.value)} placeholder="Référence" style={{ padding: "8px 10px", borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: 12, boxSizing: "border-box" }} /><div><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Quantité</label><input
                    type="number"
                    min="1"
                    value={ligne.quantite || ""}
                    onChange={(e) => updateLigneFFE(index, "quantite", e.target.value)}
                    placeholder="1"
                    style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: 12, boxSizing: "border-box" }}
                  /></div><button type="button" onClick={() => removeLigneFFE(index)} style={{ padding: "8px 12px", background: "transparent", border: `1px solid ${BORDER}`, borderRadius: 6, fontSize: 11, color: INK_MUTED, cursor: "pointer" }}>Supprimer</button></div>
              {sourcing.error && (
                <div style={{ fontSize: 11, color: RED, marginBottom: 6 }}>{sourcing.error}</div>
              )}
              {sourcing.loading && (
                <div style={{ fontSize: 11, color: INK_MUTED, marginBottom: 6 }}>Recherche produit…</div>
              )}
              {!sourcing.loading && sourcing.status === "ambiguous" && (
                <div style={{ fontSize: 11, color: INK_MUTED, marginBottom: 6 }}>
                  Résultat ambigu : plusieurs références possibles. Précisez la recherche (modèle, dimensions, finition, couleur, marchand…),
                  puis relancez « Rechercher avec IA ».
                </div>
              )}
              <div style={{ marginBottom: 6 }}><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 3 }}>
                  Préciser la recherche
                </label><input
                  value={sourcing.clarification || ""}
                  onChange={(e) =>
                    setFfeLineSourcing((prev) => ({
                      ...prev,
                      [key]: { ...(prev[key] || {}), clarification: e.target.value },
                    }))
                  }
                  placeholder="Modèle exact, dimensions, finition, couleur, marchand prioritaire…"
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    borderRadius: 6,
                    border: `1px solid ${BORDER}`,
                    fontSize: 11,
                    boxSizing: "border-box",
                    background: WHITE,
                  }}
                /></div>
              {!sourcing.loading && sourcing.results && sourcing.results.length > 0 && (
                <div style={{ marginBottom: 8, padding: "8px 10px", borderRadius: 8, background: WHITE, border: `1px solid ${BORDER}` }}><div style={{ fontSize: 11, fontWeight: 700, color: INK_SOFT, marginBottom: 6 }}>Résultats suggérés</div>
                  {sourcing.results.map((prod) => (
                    <div
                      key={prod.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "6px 0",
                        borderTop: "1px solid " + BORDER,
                      }}
                    ><div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 6,
                          overflow: "hidden",
                          background: SOFT_BG,
                          border: `1px solid ${BORDER}`,
                          flexShrink: 0,
                        }}
                      ><img
                          src={prod.image}
                          alt={prod.nom}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        /></div><div style={{ flex: 1, fontSize: 11, color: INK_SOFT }}><div style={{ fontWeight: 700, color: INK }}>{prod.nom}</div><div style={{ color: INK_MUTED }}>{prod.marque}</div>
                        {prod.reference && (
                          <div style={{ color: INK_MUTED }}>Réf. {prod.reference}</div>
                        )}
                        <div style={{ color: INK_MUTED }}>{prod.marchand}</div><div style={{ color: INK_SOFT, marginTop: 2 }}>
                          Prix public : <strong>{prod.prix.toLocaleString("fr-FR")} {prod.devise}</strong></div><div style={{ color: INK_MUTED, marginTop: 2 }}>
                          Confiance :{" "}
                          <span style={{ fontWeight: 600 }}>
                            {prod.confiance || "moyenne"}
                          </span></div></div><button
                        type="button"
                        onClick={() => appliquerProduitALigne(ligne, index, prod)}
                        style={{
                          padding: "6px 8px",
                          borderRadius: 8,
                          border: `1px solid ${GOLD}`,
                          background: GOLD_BG,
                          color: GOLD,
                          fontSize: 10,
                          fontWeight: 700,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Utiliser ce produit
                      </button></div>
                  ))}
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 10 }}><div><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Prix achat HT (DH)</label><input type="number" min="0" value={ligne.prix_achat_ht || ""} onChange={(e) => updateLigneFFE(index, "prix_achat_ht", e.target.value)} placeholder="0" style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: 12, boxSizing: "border-box" }} /></div><div><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Type produit</label><select value={ligne.type_produit || ""} onChange={(e) => updateLigneFFE(index, "type_produit", e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: 12, background: WHITE }}>
                    {FFEE_TYPE_PRODUIT.map((t) => (<option key={t.id || "empty"} value={t.id}>{t.label}</option>))}
                  </select></div><div><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Provenance</label><select value={ligne.provenance_produit || ""} onChange={(e) => updateLigneFFE(index, "provenance_produit", e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: 12, background: WHITE }}>
                    {FFEE_PROVENANCE.map((p) => (<option key={p.id || "empty"} value={p.id}>{p.label}</option>))}
                  </select></div><div><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Qualité</label><select value={ligne.qualite_produit || ""} onChange={(e) => updateLigneFFE(index, "qualite_produit", e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: 12, background: WHITE }}>
                    {FFEE_QUALITE.map((q) => (<option key={q.id || "empty"} value={q.id}>{q.label}</option>))}
                  </select></div><div><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Authenticité</label><select value={ligne.authenticite_produit || ""} onChange={(e) => updateLigneFFE(index, "authenticite_produit", e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: 12, background: WHITE }}>
                    {FFEE_AUTHENTICITE.map((a) => (<option key={a.id || "empty"} value={a.id}>{a.label}</option>))}
                  </select></div><div><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Marge % (optionnel, sinon recommandée)</label><input type="number" min={fourchette.min} max={fourchette.max} step="0.5" value={ligne.marge_percent ?? ""} onChange={(e) => updateLigneFFE(index, "marge_percent", e.target.value)} placeholder={String(comp.margeRecommandeePct)} style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: 12, boxSizing: "border-box" }} /></div><div style={{ gridColumn: "span 2" }}><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Logistique</label><div style={{ display: "flex", gap: 8, alignItems: "center" }}><select value={ligne.logistique_type || "percent"} onChange={(e) => updateLigneFFE(index, "logistique_type", e.target.value)} style={{ padding: "8px 10px", borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: 12, background: WHITE }}><option value="percent">%</option><option value="fixe">Fixe</option></select>
                    {ligne.logistique_type === "fixe" ? (
                      <input type="number" min="0" value={ligne.logistique_fixe ?? ""} onChange={(e) => updateLigneFFE(index, "logistique_fixe", e.target.value)} placeholder="Montant fixe" style={{ flex: 1, minWidth: 0, padding: "8px 10px", borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: 12, boxSizing: "border-box" }} />
                    ) : (
                      <input type="number" min="0" step="0.1" value={ligne.logistique_percent ?? ""} onChange={(e) => updateLigneFFE(index, "logistique_percent", e.target.value)} placeholder="%" style={{ flex: 1, minWidth: 0, padding: "8px 10px", borderRadius: 6, border: `1px solid ${BORDER}`, fontSize: 12, boxSizing: "border-box" }} />
                    )}
                  </div></div></div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr auto", gap: 12, alignItems: "end", marginTop: 12, paddingTop: 12, borderTop: `1px solid ${BORDER}` }}><div><div style={{ fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Prix public</div><div style={{ fontSize: 14, fontWeight: 600 }}>
                    {(ligne.prix_public ? Number(ligne.prix_public) : comp.prixHT).toLocaleString("fr-FR")} {deviseLabel}
                  </div></div><div><div style={{ fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Marge recommandée</div><div style={{ fontSize: 14, fontWeight: 600 }}>{comp.margeRecommandeePct} %</div></div><div><div style={{ fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Total marge</div><div style={{ fontSize: 14, fontWeight: 600 }}>{comp.margeDH.toLocaleString("fr-FR")} {deviseLabel}</div></div><div><div style={{ fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Prix client</div><div style={{ fontSize: 15, fontWeight: 700, color: GOLD }}>{comp.totalClient.toLocaleString("fr-FR")} {deviseLabel}</div></div><div style={{ gridColumn: "span 1", minWidth: 0 }} /></div><div style={{ marginTop: 10, fontSize: 11, color: INK_SOFT, fontStyle: "italic" }}>{comp.justification}</div></div>
          );
        })}
        <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}><button
            type="button"
            onClick={addLigneFFE}
            style={{
              padding: "10px 16px",
              background: SOFT_BG,
              border: `1px solid ${BORDER}`,
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              color: INK_SOFT,
              cursor: "pointer",
            }}
          >
            + Ajouter une ligne FF&E
          </button><label
            style={{
              padding: "10px 16px",
              background: WHITE,
              border: `1px solid ${BORDER}`,
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              color: INK_SOFT,
              cursor: "pointer",
            }}
          >
            Importer CSV
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={importerFfeDepuisCsv}
              style={{ display: "none" }}
            /></label></div></div><div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "24px 28px", marginBottom: 20 }}><div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: GOLD, fontWeight: 700, marginBottom: 16 }}>6. Zone 4 — Frais annexes</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}><div><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Frais de déplacement (DH)</label><input type="number" min="0" value={form.frais_deplacement} onChange={(e) => update("frais_deplacement", e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, boxSizing: "border-box" }} /></div><div><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Impressions / maquettes (DH)</label><input type="number" min="0" value={form.impressions_maquettes} onChange={(e) => update("impressions_maquettes", e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, boxSizing: "border-box" }} /></div><div><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Provision imprévus (DH)</label><input type="number" min="0" value={form.provision_imprevus} onChange={(e) => update("provision_imprevus", e.target.value)} placeholder="5 % budget par défaut" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, boxSizing: "border-box" }} /></div></div></div><div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "24px 28px", marginBottom: 20 }}><div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: GOLD, fontWeight: 700, marginBottom: 16 }}>7. Tableau de synthèse</div><p style={{ fontSize: 12, color: INK_MUTED, marginBottom: 12 }}>Uniquement les prestations activées et dont la condition est remplie.</p><div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}><thead><tr style={{ borderBottom: `2px solid ${BORDER}` }}><th style={{ textAlign: "left", padding: "10px 8px", fontWeight: 700, color: INK_MUTED }}>Poste</th><th style={{ textAlign: "left", padding: "10px 8px", fontWeight: 700, color: INK_MUTED }}>Détail</th><th style={{ textAlign: "right", padding: "10px 8px", fontWeight: 700, color: INK_MUTED }}>Quantité / base</th><th style={{ textAlign: "right", padding: "10px 8px", fontWeight: 700, color: INK_MUTED }}>Prix unitaire</th><th style={{ textAlign: "right", padding: "10px 8px", fontWeight: 700, color: INK_MUTED }}>Total HT</th></tr></thead><tbody>
              {devisSynthèse.rows.map((row, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${BORDER}` }}><td style={{ padding: "8px" }}>{row.poste}</td><td style={{ padding: "8px" }}>{row.detail}</td><td style={{ padding: "8px", textAlign: "right" }}>{row.base_calcul ?? (typeof row.quantite_base === "number" ? row.quantite_base : row.quantite_base)}</td><td style={{ padding: "8px", textAlign: "right" }}>{typeof row.prix_unitaire === "number" ? row.prix_unitaire.toLocaleString("fr-FR") + (row.unite ? " " + row.unite : "") : row.prix_unitaire}</td><td style={{ padding: "8px", textAlign: "right", fontWeight: 600 }}>{row.total_ht.toLocaleString("fr-FR")} {deviseLabel}</td></tr>
              ))}
            </tbody></table></div><div style={{ marginTop: 16, display: "grid", gap: 8 }}><div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><span style={{ color: INK_MUTED }}>Total honoraires</span><strong>{devisSynthèse.totalHonoraires.toLocaleString("fr-FR")} {deviseLabel}</strong></div><div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><span style={{ color: INK_MUTED }}>Total FF&E</span><strong>{devisSynthèse.totalFFE.toLocaleString("fr-FR")} {deviseLabel}</strong></div><div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><span style={{ color: INK_MUTED }}>Total frais annexes</span><strong>{devisSynthèse.totalFraisAnnexes.toLocaleString("fr-FR")} {deviseLabel}</strong></div><div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, paddingTop: 12, borderTop: `2px solid ${BORDER}`, marginTop: 8 }}><span style={{ fontWeight: 700, color: INK }}>Total général HT</span><span style={{ fontWeight: 800, color: GOLD }}>{devisSynthèse.totalGeneralHT.toLocaleString("fr-FR")} {deviseLabel}</span></div></div><div style={{ marginTop: 16 }}><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Montant proposé (enregistré)</label><div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}><input type="number" min="0" value={form.montant} onChange={(e) => update("montant", e.target.value)} placeholder={String(devisSynthèse.totalGeneralHT)} style={{ width: 160, padding: "10px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, boxSizing: "border-box" }} /><button type="button" onClick={appliquerTotalDevis} style={{ padding: "10px 14px", background: SOFT_BG, border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 12, fontWeight: 600, color: INK_SOFT, cursor: "pointer" }}>Utiliser total devis</button></div></div></div><div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "24px 28px", marginBottom: 20 }}><div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: GOLD, fontWeight: 700, marginBottom: 16 }}>8. Acompte (%)</div><input type="number" min="0" max="100" value={form.acompte_pct} onChange={(e) => update("acompte_pct", e.target.value)} placeholder="30" style={{ width: "100%", maxWidth: 120, padding: "10px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, boxSizing: "border-box" }} /></div><div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "24px 28px", marginBottom: 20 }}><div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: GOLD, fontWeight: 700, marginBottom: 16 }}>9. Échéancier simple</div><div style={{ display: "flex", flexDirection: "column", gap: 12 }}><div><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Acompte</label><input value={form.echeancier_acompte} onChange={(e) => update("echeancier_acompte", e.target.value)} placeholder="Acompte" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, boxSizing: "border-box" }} /></div><div><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Lancement</label><input value={form.echeancier_lancement} onChange={(e) => update("echeancier_lancement", e.target.value)} placeholder="Lancement" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, boxSizing: "border-box" }} /></div><div><label style={{ display: "block", fontSize: 10, fontWeight: 700, color: INK_MUTED, marginBottom: 4 }}>Fin projet</label><input value={form.echeancier_fin_projet} onChange={(e) => update("echeancier_fin_projet", e.target.value)} placeholder="Fin projet" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, boxSizing: "border-box" }} /></div></div></div><div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "24px 28px", marginBottom: 20 }}><div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: GOLD, fontWeight: 700, marginBottom: 16 }}>10. Statut de la proposition</div><select value={form.statut} onChange={(e) => update("statut", e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13, background: WHITE }}>
          {STATUTS.map((s) => (<option key={s.id} value={s.id}>{s.label}</option>))}
        </select></div>

      {saveMessage && (
        <div
          style={{
            marginBottom: 20,
            padding: "14px 18px",
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 600,
            background: saveMessage.type === "success" ? GREEN_BG : RED_BG,
            color: saveMessage.type === "success" ? GREEN : RED,
            border: `1px solid ${saveMessage.type === "success" ? GREEN : RED}`,
          }}
        >
          {saveMessage.text}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}><button
          type="button"
          onClick={genererPdf}
          style={{
            padding: "12px 18px",
            background: SOFT_BG,
            border: `1px solid ${BORDER}`,
            borderRadius: 10,
            fontSize: 12,
            fontWeight: 600,
            color: INK_SOFT,
            cursor: "pointer",
          }}
        >
          Générer PDF
        </button><button onClick={enregistrer} disabled={saving} style={{ padding: "12px 24px", background: saving ? "#DDD" : GOLD, border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, color: WHITE, cursor: saving ? "not-allowed" : "pointer" }}>
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button></div></div></div>
  );
}
